import React from "react";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, CheckCircle, XCircle, Info } from "lucide-react";
import { CreditManagementService } from "@/services/CreditManagementService";

interface CreditStatusBadgeProps {
  currentBalance: number;
  creditLimit: number;
  showDetails?: boolean;
  className?: string;
}

export const CreditStatusBadge: React.FC<CreditStatusBadgeProps> = ({
  currentBalance,
  creditLimit,
  showDetails = false,
  className = "",
}) => {
  const creditStatus = CreditManagementService.getCreditStatus(
    currentBalance,
    creditLimit
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-TN", {
      style: "currency",
      currency: "TND",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getBadgeVariant = () => {
    switch (creditStatus.status) {
      case "good":
        return "default";
      case "warning":
        return "secondary";
      case "over_limit":
        return "destructive";
      case "no_limit":
        return "outline";
      default:
        return "default";
    }
  };

  const getBadgeText = () => {
    switch (creditStatus.status) {
      case "good":
        return "Good Standing";
      case "warning":
        return `${creditStatus.utilization.toFixed(0)}% Used`;
      case "over_limit":
        return "Over Limit";
      case "no_limit":
        return "No Limit";
      default:
        return "Unknown";
    }
  };

  const getIcon = () => {
    switch (creditStatus.status) {
      case "good":
        return <CheckCircle className="h-3 w-3" />;
      case "warning":
        return <AlertTriangle className="h-3 w-3" />;
      case "over_limit":
        return <XCircle className="h-3 w-3" />;
      case "no_limit":
        return <Info className="h-3 w-3" />;
      default:
        return null;
    }
  };

  if (showDetails) {
    return (
      <div className={`space-y-2 ${className}`}>
        <div className="flex items-center space-x-2">
          <Badge
            variant={getBadgeVariant()}
            className="flex items-center space-x-1"
          >
            {getIcon()}
            <span>{getBadgeText()}</span>
          </Badge>
        </div>

        {creditStatus.status === "over_limit" && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              This client has exceeded their credit limit by{" "}
              {formatCurrency(currentBalance - creditLimit)}. Current balance:{" "}
              {formatCurrency(currentBalance)}, Limit:{" "}
              {formatCurrency(creditLimit)}
            </AlertDescription>
          </Alert>
        )}

        {creditStatus.status === "warning" && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              This client is using {creditStatus.utilization.toFixed(1)}% of
              their credit limit. Available credit:{" "}
              {formatCurrency(creditStatus.availableCredit)}
            </AlertDescription>
          </Alert>
        )}

        {creditStatus.status === "no_limit" && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              No credit limit set for this client. Current balance:{" "}
              {formatCurrency(currentBalance)}
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  return (
    <Badge
      variant={getBadgeVariant()}
      className={`flex items-center space-x-1 ${className}`}
    >
      {getIcon()}
      <span>{getBadgeText()}</span>
    </Badge>
  );
};
